import { API, Auth } from 'aws-amplify';

const query = /* GraphQL */ `
  query GetPlatformHealth($brands: [Int!], $services: [String!], $date: AWSDateTime) {
    getPlatformHealth(brands: $brands, services: $services, date: $date) {
      ... on GetPlatformHealthSuccess {
        __typename
        result {
          PlatformHealth {
            Overall {
              health
              healthPercentage
              failureRatio
              date
              e2eTestsUpdatedAt
              healthUpdatedAt
              lastNonGoodHealth {
                health
                healthPercentage
                healthUpdatedAt
              }
            }
            Service {
              service
              failureRatio
              health
              healthPercentage
              healthUpdatedAt
              failureDetails {
                type
                name
                criticality
                errorCategory
                brandImportance
                serviceRelevance
                errorFrequency
                errorSeverity
              }
              links {
                tests
                alarms
              }
            }
          }
          BrandHealth {
            Brand
            Overall {
              health
              healthPercentage
              failureRatio
              date
              e2eTestsUpdatedAt
              healthUpdatedAt
              lastNonGoodHealth {
                health
                healthPercentage
                healthUpdatedAt
              }
            }
            Service {
              service
              failureRatio
              health
              healthPercentage
              healthUpdatedAt
              failureDetails {
                type
                name
                criticality
                errorCategory
                brandImportance
                serviceRelevance
                errorFrequency
                errorSeverity
              }
              links {
                tests
                alarms
              }
            }
          }
        }
      }
      ... on BadRequest {
        __typename
        message
        statusCode
      }
      ... on Forbidden {
        __typename
        message
      }
      ... on InternalServerError {
        __typename
        message
        statusCode
      }
      ... on NotFound {
        __typename
        message
      }
    }
  }
`;

export interface GetPlatformHealthVariables {
  brands?: number[];
  services?: string[];
  date?: string;
}

interface PlatformHealthResult {
  PlatformHealth: {
    Overall: {
      health: string;
      healthPercentage: number;
      failureRatio: number;
      date: string;
      e2eTestsUpdatedAt: string;
      healthUpdatedAt: string;
      lastNonGoodHealth: {
        health: string;
        healthPercentage: number;
        healthUpdatedAt: string;
      };
    };
    Service: {
      service: string;
      failureRatio: number;
      health: string;
      healthPercentage: number;
      healthUpdatedAt: string;
      failureDetails: {
        type: string;
        name: string;
        criticality: string;
        errorCategory: string;
        brandImportance: string;
        serviceRelevance: string;
        errorFrequency: string;
        errorSeverity: string;
      }[];
      links: {
        tests: string;
        alarms: string;
      };
    }[];
  };
  BrandHealth: {
    Brand: string;
    Overall: {
      health: string;
      healthPercentage: number;
      failureRatio: number;
      date: string;
      e2eTestsUpdatedAt: string;
      healthUpdatedAt: string;
      lastNonGoodHealth: {
        health: string;
        healthPercentage: number;
        healthUpdatedAt: string;
      };
    };
    Service: {
      service: string;
      failureRatio: number;
      health: string;
      healthPercentage: number;
      healthUpdatedAt: string;
      failureDetails: {
        type: string;
        name: string;
        criticality: string;
        errorCategory: string;
        brandImportance: string;
        serviceRelevance: string;
        errorFrequency: string;
        errorSeverity: string;
      }[];
      links: {
        tests: string;
        alarms: string;
      };
    }[];
  }[];
}

async function getPlatformHealthService({
  brands,
  services,
  date,
}: GetPlatformHealthVariables): Promise<PlatformHealthResult> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const postData = await API.graphql({
      query,
      variables: {
        brands,
        services,
        date,
      },
      authToken: user.signInUserSession.accessToken.jwtToken,
    }) as { data: { getPlatformHealth: any } };

    if (!postData.data) {
      throw new Error('Internal Server Error');
    }

    const response = postData.data.getPlatformHealth;

    if (response.__typename === 'GetPlatformHealthSuccess') {
      return response.result;
    }

    throw new Error(response.message);
  } catch (error: any) {
    console.error('Error in getPlatformHealth:', error);
    throw new Error(`Internal Server Error: ${error.errors ? error.errors[0].message : error.message}`);
  }
}

export default getPlatformHealthService;